// src/components/Login.js
import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { verificarUsuario, actualizarUltimoAcceso, calcularDiasRestantes, actualizarEstado } from '../services/userService';
import { Mail, KeyRound, Eye, EyeOff, ArrowRight, LockKeyhole, ShieldCheck } from 'lucide-react';
import './login24.css';

const Login = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [rememberSession, setRememberSession] = useState(true);
  const saveSession = (userData) => {
    localStorage.removeItem('nuvia_user');
    sessionStorage.removeItem('nuvia_user');
    (rememberSession ? localStorage : sessionStorage).setItem('nuvia_user', JSON.stringify(userData));
  };
  const [credentials, setCredentials] = useState({
    correo: '',
    ruc: ''
  });


  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const email = decoded.email;
      console.log('👤 Intentando login con Google para:', email);

      const verificacion = await verificarUsuario(email);

      if (verificacion.autorizado) {
        const userData = {
          email: decoded.email,
          name: decoded.name,
          picture: decoded.picture,
          plan: verificacion.usuario.plan,
          curso_tipo: verificacion.usuario.curso_tipo,
          empresa: verificacion.usuario.empresa,
          celular: verificacion.usuario.celular,
          ruc: verificacion.usuario.ruc,
          dias_restantes: verificacion.usuario.dias_restantes,
          estado: verificacion.usuario.estado
        };

        saveSession(userData);
        actualizarUltimoAcceso(decoded.email);
        console.log('✅ Login con Google exitoso');
        onLoginSuccess(userData);
      } else {
        setError(verificacion.mensaje);
        console.log('❌ Login con Google fallido:', verificacion.mensaje);
      }
    } catch (err) {
      console.error('❌ Error en login con Google:', err);
      setError('Error al verificar credenciales: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialsLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const correoNormalizado = credentials.correo.trim().toLowerCase();
      console.log('👤 Intentando login con credenciales para:', correoNormalizado);
      const verificacion = await verificarUsuarioPorCredenciales(correoNormalizado, credentials.ruc.trim());

      if (verificacion.autorizado) {
        const userData = {
          email: verificacion.usuario.correo,
          name: verificacion.usuario.empresa,
          picture: null,
          plan: verificacion.usuario.plan,
          curso_tipo: verificacion.usuario.curso_tipo,
          empresa: verificacion.usuario.empresa,
          celular: verificacion.usuario.celular,
          ruc: verificacion.usuario.ruc,
          dias_restantes: verificacion.usuario.dias_restantes,
          estado: verificacion.usuario.estado
        };

        saveSession(userData);
        actualizarUltimoAcceso(verificacion.usuario.correo);
        console.log('✅ Login con credenciales exitoso');
        onLoginSuccess(userData);
      } else {
        setError(verificacion.mensaje);
        console.log('❌ Login con credenciales fallido:', verificacion.mensaje);
      }
    } catch (err) {
      console.error('❌ Error en login con credenciales:', err);
      setError('Error al verificar credenciales: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // La comprobación ocurre dentro de la base (función login_portal): el navegador
  // nunca recibe la tabla de usuarios, solo los datos de quien acierta la clave.
  const verificarUsuarioPorCredenciales = async (correo, clave) => {
    try {
      const { supabase } = await import('../services/supabaseClient');
      const { data, error } = await supabase.rpc('login_portal', {
        p_correo: correo,
        p_clave: clave
      });

      if (error) {
        console.error('Error consultando credenciales:', error);
        return {
          autorizado: false,
          mensaje: 'Error al verificar credenciales. Intenta nuevamente.',
          usuario: null
        };
      }

      if (!data) {
        return {
          autorizado: false,
          mensaje: 'Credenciales incorrectas. Verifica tu correo y código único.',
          usuario: null
        };
      }

      const diasRestantes = calcularDiasRestantes(data.fecha_fin);
      const usuarioCompleto = {
        ...data,
        dias_restantes: diasRestantes,
        estado: actualizarEstado(diasRestantes)
      };

      if (diasRestantes <= 0) {
        return {
          autorizado: false,
          mensaje: `Tu suscripción venció hace ${Math.abs(diasRestantes)} días. Contacta al administrador para renovar.`,
          usuario: usuarioCompleto
        };
      }

      return { autorizado: true, mensaje: 'Acceso autorizado', usuario: usuarioCompleto };

    } catch (error) {
      console.error('Error verificando credenciales:', error);
      throw new Error('Error al verificar credenciales en la base de datos');
    }
  };

  const handleGoogleError = () => {
    console.log('❌ Error en Google OAuth');
    setError('Error al autenticar con Google');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <main className="nuvia-login">
      <aside className="nuvia-login__intro" aria-labelledby="login-intro-title">
        <div className="nuvia-login__brand">
          <img src="/nuvia-200x200.jpg" alt="" width="38" height="38" />
          <div>
            <strong>Nuvia Consulting</strong>
            <span>Portal de Contrataciones</span>
          </div>
        </div>
        <div className="nuvia-login__pitch">
          <h2 id="login-intro-title">¿Quieres contratar con el estado y te cuesta buscar oportunidades?</h2>
          <p>Encuentra las oportunidades de negocio con el estado en un solo lugar.</p>
          <dl className="nuvia-login__metrics">
            <div><dt>Convocatorias nuevas diariamente</dt><dd>+400</dd></div>
            <div><dt>Promedio más de ventas</dt><dd>40%</dd></div>
            <div><dt>Empresas suscritas</dt><dd>500+</dd></div>
          </dl>
        </div>
        <div className="nuvia-login__trust">
          <span><LockKeyhole size={16} aria-hidden="true" />Conexión cifrada</span>
          <span><ShieldCheck size={16} aria-hidden="true" />Datos no compartidos</span>
        </div>
      </aside>
      <div className="nuvia-login__form-side">
      <section className="nuvia-login__panel" aria-labelledby="login-title">
        <header className="nuvia-login__header">
          <h1 id="login-title">Inicia sesión</h1>
          <p>Ingresa con el correo y el código único que recibiste al suscribirte.</p>
        </header>
        <form onSubmit={handleCredentialsLogin} aria-busy={loading}>
          <div className="nuvia-login__field">
            <label htmlFor="login-email">Correo electrónico</label>
            <div className="nuvia-login__input">
              <Mail size={19} aria-hidden="true" />
              <input id="login-email" type="email" name="correo" autoComplete="username"
                placeholder="tucorreo@empresa.com" value={credentials.correo}
                onChange={handleInputChange} required disabled={loading} />
            </div>
          </div>
          <div className="nuvia-login__field">
            <div className="nuvia-login__label-row">
              <label htmlFor="login-code">Código único</label>
              <a href="https://wa.me/51942128139?text=Hola%2C%20necesito%20recuperar%20mi%20codigo%20de%20acceso."
                target="_blank" rel="noopener noreferrer">¿Lo olvidaste?</a>
            </div>
            <div className="nuvia-login__input">
              <KeyRound size={19} aria-hidden="true" />
              <input id="login-code" type={showCode ? 'text' : 'password'} name="ruc"
                autoComplete="current-password" placeholder="••••••••" aria-describedby="login-code-hint"
                value={credentials.ruc} onChange={handleInputChange} required disabled={loading} />
              <button className="nuvia-login__visibility" type="button"
                aria-label={showCode ? 'Ocultar código' : 'Mostrar código'} aria-pressed={showCode}
                onClick={() => setShowCode(!showCode)} disabled={loading}>
                {showCode ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>
            <p className="nuvia-login__hint" id="login-code-hint">Usa el código de acceso que recibiste al suscribirte.</p>
          </div>
          <label className="nuvia-login__remember">
            <input type="checkbox" checked={rememberSession}
              onChange={(e) => setRememberSession(e.target.checked)} disabled={loading} />
            <span>Mantener sesión iniciada en este equipo</span>
          </label>
          {error && <div className="nuvia-login__error" role="alert">{error}</div>}
          <button className="nuvia-login__submit" type="submit" disabled={loading}>
            <span>{loading ? 'Verificando...' : 'Iniciar sesión'}</span>
            {!loading && <ArrowRight size={19} aria-hidden="true" />}
          </button>
          <span className="nuvia-login__status" role="status">{loading ? 'Verificando credenciales...' : ''}</span>
        </form>
        <div className="nuvia-login__separator"><span>o continúa con</span></div>
        <fieldset className="nuvia-login__google" disabled={loading} aria-label="Acceso con Google">
          <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError}
            text="continue_with" shape="rectangular" theme="outline" size="large" locale="es" width="280" />
        </fieldset>
        <p className="nuvia-login__access">¿Aún no tienes cuenta?{' '}
          <a href="https://wa.me/51942128139?text=Hola%2C%20quiero%20solicitar%20acceso%20al%20portal%20Nuvia."
            target="_blank" rel="noopener noreferrer">Solicita acceso</a>
        </p>
      </section>
      </div>
    </main>
  );
};
export default Login;
